package fyi.moko.hotpl.ui.directory

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.Place
import androidx.compose.material.icons.filled.Search
import androidx.compose.material3.AssistChip
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.FilterChip
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.platform.LocalSoftwareKeyboardController
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import fyi.moko.hotpl.data.location.CurrentLocationProvider
import fyi.moko.hotpl.data.network.NEARBY_BROWSE_CATEGORIES
import fyi.moko.hotpl.domain.model.PlaceCategory
import fyi.moko.hotpl.domain.model.SearchPrediction
import fyi.moko.hotpl.domain.model.YellowPageEntry
import fyi.moko.hotpl.ui.components.SectionHeader
import fyi.moko.hotpl.ui.detail.rememberOpenInMapsLauncher
import fyi.moko.hotpl.ui.isIosPlatform
import fyi.moko.hotpl.ui.home.rememberLocationPermissionRequest
import io.ktor.http.encodeURLParameter
import kotlinx.coroutines.launch
import org.koin.compose.koinInject
import org.koin.compose.viewmodel.koinViewModel

/**
 * Directory (Yellow Pages) tab screen.
 *
 * Shows an offline search field over the cached directory entries, falling back to
 * live Google Places autocomplete when the local cache has fewer than three matches.
 * Each row exposes an "Add to journal" action that clones the entry into the user's
 * private journal and also submits Google results to the moderation queue, then hands
 * the new journal id to [onOpenPlace] so the user lands on the place and keeps going
 * (photos, a note) instead of being left on the search results (0-24).
 */
@Composable
fun DirectoryScreen(
    modifier: Modifier = Modifier,
    onOpenPlace: (placeId: String) -> Unit = {},
) {
    val viewModel: DirectoryViewModel = koinViewModel()
    val query by viewModel.searchQuery.collectAsStateWithLifecycle()
    val destinationKeyword by viewModel.destinationKeyword.collectAsStateWithLifecycle()
    val recentDestinations by viewModel.recentDestinations.collectAsStateWithLifecycle()
    val entries by viewModel.directoryEntries.collectAsStateWithLifecycle()
    val googlePredictions by viewModel.googlePredictions.collectAsStateWithLifecycle()
    val onlineResultsAttribution by viewModel.onlineResultsAttribution.collectAsStateWithLifecycle()
    val isSyncing by viewModel.isSyncing.collectAsStateWithLifecycle()
    val isSearchingRemote by viewModel.isSearchingRemote.collectAsStateWithLifecycle()
    val resultsAreNearestFirst by viewModel.resultsAreNearestFirst.collectAsStateWithLifecycle()
    val selectedNearbyCategory by viewModel.selectedNearbyCategory.collectAsStateWithLifecycle()
    val origin by viewModel.origin.collectAsStateWithLifecycle()
    val importInProgress by viewModel.importInProgress.collectAsStateWithLifecycle()
    val message by viewModel.uiMessage.collectAsStateWithLifecycle()

    val snackbarHostState = remember { SnackbarHostState() }
    val scope = rememberCoroutineScope()
    val uriHandler = LocalUriHandler.current
    val openInMaps = rememberOpenInMapsLauncher()
    val keyboardController = LocalSoftwareKeyboardController.current
    val focusManager = LocalFocusManager.current
    val resultsListState = rememberLazyListState()

    fun dismissKeyboard() {
        keyboardController?.hide()
        focusManager.clearFocus()
    }

    // Both distance-ranked actions need a location, and this screen never asked for the
    // permission — it just called the provider and reported "Location unavailable" when the
    // answer was no, which is what a user who has never opened the Journal's Nearest sort
    // sees every single time. Ask first, then run what they tapped.
    val locationProvider: CurrentLocationProvider = koinInject()
    var pendingLocationAction by remember { mutableStateOf<(() -> Unit)?>(null) }
    val permissionRequester = rememberLocationPermissionRequest { granted ->
        val action = pendingLocationAction
        pendingLocationAction = null
        if (granted) {
            action?.invoke()
        } else {
            scope.launch {
                snackbarHostState.showSnackbar("Location permission is needed to rank places by distance")
            }
        }
    }

    fun withLocation(action: () -> Unit) {
        if (locationProvider.isPermissionGranted()) {
            action()
        } else {
            pendingLocationAction = action
            permissionRequester.launch()
        }
    }

    LaunchedEffect(message) {
        message?.let { msg ->
            scope.launch { snackbarHostState.showSnackbar(msg) }
            viewModel.clearUiMessage()
        }
    }

    // Dragging the results list is a strong signal the user is done typing — the keyboard
    // was covering up to a third of the screen (fewer than 2 result rows visible), so hide
    // it as soon as a scroll starts rather than waiting for an explicit dismiss action.
    LaunchedEffect(resultsListState.isScrollInProgress) {
        if (resultsListState.isScrollInProgress) {
            dismissKeyboard()
        }
    }

    // Box so the SnackbarHost can overlay the content: this screen lives inside
    // HomeScreen's tab content, not its own Scaffold, so without an explicitly placed
    // host every showSnackbar() call would be silently dropped.
    Box(modifier = modifier.fillMaxSize()) {
        Column(
            modifier = Modifier.fillMaxSize().padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            val pillFieldColors = OutlinedTextFieldDefaults.colors(
                unfocusedContainerColor = MaterialTheme.colorScheme.surfaceContainerLow,
                focusedContainerColor = MaterialTheme.colorScheme.surfaceContainerLow,
                unfocusedBorderColor = MaterialTheme.colorScheme.outlineVariant,
            )
            OutlinedTextField(
                value = query,
                onValueChange = { viewModel.onSearchQueryChange(it) },
                modifier = Modifier.fillMaxWidth(),
                shape = RoundedCornerShape(50),
                colors = pillFieldColors,
                placeholder = { Text("Search places") },
                leadingIcon = { Icon(Icons.Default.Search, contentDescription = null, tint = MaterialTheme.colorScheme.outline) },
                singleLine = true,
                keyboardOptions = KeyboardOptions(imeAction = ImeAction.Search),
                // Submitting is a different request from the typeahead above it: this one is
                // ranked by distance, which is the only way the nearest branch of a chain is
                // guaranteed to appear at all.
                keyboardActions = KeyboardActions(onSearch = {
                    withLocation { viewModel.onSearchSubmitted() }
                    dismissKeyboard()
                }),
            )

            OutlinedTextField(
                value = destinationKeyword,
                onValueChange = { viewModel.onDestinationKeywordChange(it) },
                modifier = Modifier.fillMaxWidth(),
                shape = RoundedCornerShape(50),
                colors = pillFieldColors,
                placeholder = { Text("Destination area, e.g. Culver City (optional)") },
                leadingIcon = { Icon(Icons.Default.Place, contentDescription = null, tint = MaterialTheme.colorScheme.outline) },
                singleLine = true,
                keyboardOptions = KeyboardOptions(imeAction = ImeAction.Search),
                keyboardActions = KeyboardActions(onSearch = {
                    viewModel.onDestinationSubmitted()
                    dismissKeyboard()
                }),
            )

            Text(
                text = originLabel(origin, viewModel.nearbyBrowseAvailable),
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                modifier = Modifier.testTag("directory-origin"),
            )
            // "Near me" on its own returned everything at once — a grocery store above the
            // cafe next door. Each category re-runs the browse asking Google for that kind
            // only; tapping the active one drops back to the unfiltered browse.
            //
            // 쿼리 없는 탐색을 못 하는 제공자에서는 이 줄 전체를 감춘다. 웹은 Places 키가 없어
            // Nominatim으로 떨어지는데 거기엔 그런 엔드포인트가 아예 없어서, 칩이 눌리기는 하고
            // 늘 빈 결과가 왔다 — 그리고 화면이 그걸 "주변에 없다"고 잘못 말했다. 실제로는
            // "이 제공자는 그 질문에 답할 수 없다"였다. 안 되는 버튼을 보여 주느니 감춘다.
            if (viewModel.nearbyBrowseAvailable) {
                Row(
                    modifier = Modifier.fillMaxWidth().horizontalScroll(rememberScrollState()),
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    FilterChip(
                        selected = resultsAreNearestFirst && selectedNearbyCategory == null,
                        onClick = {
                            withLocation { viewModel.browseNearby() }
                            dismissKeyboard()
                        },
                        leadingIcon = {
                            Icon(Icons.Default.Place, contentDescription = null, modifier = Modifier.size(18.dp))
                        },
                        label = { Text("Near me") },
                    )
                    NEARBY_BROWSE_CATEGORIES.forEach { category ->
                        FilterChip(
                            selected = resultsAreNearestFirst && selectedNearbyCategory == category,
                            onClick = {
                                withLocation { viewModel.browseNearby(category) }
                                dismissKeyboard()
                            },
                            label = { Text(category.label) },
                        )
                    }
                }
            }

            if (recentDestinations.isNotEmpty()) {
                Row(
                    modifier = Modifier.fillMaxWidth().horizontalScroll(rememberScrollState()),
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                ) {
                    recentDestinations.forEach { destination ->
                        AssistChip(
                            onClick = {
                                viewModel.onDestinationKeywordChange(destination)
                                dismissKeyboard()
                            },
                            label = { Text(destination) },
                        )
                    }
                }
            }

            if (isSyncing) {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                ) {
                    CircularProgressIndicator(modifier = Modifier.height(16.dp))
                    Text(
                        text = "Updating nearby directory...",
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                }
            }

            LazyColumn(
                modifier = Modifier.fillMaxSize(),
                state = resultsListState,
                contentPadding = PaddingValues(vertical = 4.dp),
                verticalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                // Local directory results
                if (entries.isNotEmpty()) {
                    item(key = "local-header") {
                        SectionHeader(title = "Local Directory")
                    }
                    items(entries, key = { "local-${it.entryId}" }) { entry ->
                        DirectoryEntryCard(
                            entry = entry,
                            isImporting = entry.entryId in importInProgress,
                            onImport = {
                                viewModel.importToJournal(
                                    entry = entry,
                                    onSuccess = onOpenPlace,
                                    onError = { msg ->
                                        scope.launch { snackbarHostState.showSnackbar(msg) }
                                    },
                                )
                            },
                            // A directory entry has coordinates, so the platform launcher can
                            // hand it to the maps app directly. On iOS the Google Maps https
                            // link opened nothing when the app was installed (2026-09-20), and
                            // the launcher goes through the app's own scheme instead; Android
                            // keeps the https link the reviewer already knows (place card,
                            // not just a pin). Predictions below have no coordinates yet.
                            onOpenInMaps = {
                                if (!isIosPlatform || !openInMaps(entry.latitude, entry.longitude, entry.name)) {
                                    uriHandler.openUri(googleMapsUrlFor(entry))
                                }
                            },
                        )
                    }
                }

                // Remote autocomplete fallback — provider varies (Google Places or, with no
                // API key configured, free OSM Nominatim); label stays provider-neutral.
                if (googlePredictions.isNotEmpty()) {
                    item(key = "online-header") {
                        SectionHeader(title = nearestResultsHeader(resultsAreNearestFirst, selectedNearbyCategory))
                    }
                    items(googlePredictions, key = { "google-${it.placeId}" }) { prediction ->
                        GooglePredictionRow(
                            prediction = prediction,
                            isImporting = prediction.placeId in importInProgress,
                            onImport = {
                                viewModel.importGooglePlace(
                                    prediction = prediction,
                                    onSuccess = onOpenPlace,
                                    onError = { msg ->
                                        scope.launch { snackbarHostState.showSnackbar(msg) }
                                    },
                                )
                            },
                            onOpenInMaps = { uriHandler.openUri(googleMapsUrlFor(prediction)) },
                        )
                    }
                    onlineResultsAttribution?.let { attribution ->
                        item(key = "online-attribution") {
                            Text(
                                text = attribution,
                                style = MaterialTheme.typography.labelSmall,
                                color = MaterialTheme.colorScheme.onSurfaceVariant,
                                modifier = Modifier.padding(horizontal = 16.dp, vertical = 4.dp),
                            )
                        }
                    }
                }

                if (isSearchingRemote) {
                    item(key = "remote-loading") {
                        LinearProgressIndicator(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(vertical = 8.dp),
                        )
                    }
                }

                if (entries.isEmpty() && googlePredictions.isEmpty() && query.isNotBlank() && !isSearchingRemote) {
                    item(key = "no-results") {
                        Text(
                            text = "No directory results nearby. Try a different search.",
                            style = MaterialTheme.typography.bodyMedium,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                            modifier = Modifier.padding(top = 16.dp),
                        )
                    }
                }
            }
        }

        SnackbarHost(
            hostState = snackbarHostState,
            modifier = Modifier.align(Alignment.BottomCenter),
        )
    }
}

/** Opens a Google Maps search pinned at the entry's cached coordinates. */
private fun googleMapsUrlFor(entry: YellowPageEntry): String {
    val query = "${entry.latitude},${entry.longitude}".encodeURLParameter()
    return "https://www.google.com/maps/search/?api=1&query=$query"
}

/**
 * Opens a Google Maps search pinned by Place ID — no coordinates are available for a
 * not-yet-imported autocomplete prediction, so this leans on Google resolving the
 * `query_place_id` server-side. `query` is a required fallback per Google's URL API.
 */
private fun googleMapsUrlFor(prediction: SearchPrediction): String {
    val query = prediction.primaryText.encodeURLParameter()
    return "https://www.google.com/maps/search/?api=1&query=$query&query_place_id=${prediction.placeId}"
}

@Composable
private fun DirectoryEntryCard(
    entry: YellowPageEntry,
    isImporting: Boolean,
    onImport: () -> Unit,
    onOpenInMaps: () -> Unit,
    modifier: Modifier = Modifier,
) {
    CuratedPlaceRow(
        title = entry.name,
        category = entry.tags.firstOrNull(),
        subtitle = entry.phoneNumber ?: entry.website ?: entry.tags.drop(1).joinToString(", ").ifBlank { null },
        isImporting = isImporting,
        onImport = onImport,
        onOpenInMaps = onOpenInMaps,
        modifier = modifier,
    )
}

@Composable
private fun GooglePredictionRow(
    prediction: SearchPrediction,
    isImporting: Boolean,
    onImport: () -> Unit,
    onOpenInMaps: () -> Unit,
    modifier: Modifier = Modifier,
) {
    CuratedPlaceRow(
        title = prediction.primaryText,
        category = null,
        // Distance leads the subtitle — with a chain like "starbucks" every row shares a
        // name, and how far away it is is the only thing that tells them apart.
        subtitle = listOfNotNull(
            prediction.distanceMeters?.let(::formatDistance),
            prediction.secondaryText.ifBlank { null },
        ).joinToString(" · ").ifBlank { null },
        isImporting = isImporting,
        onImport = onImport,
        onOpenInMaps = onOpenInMaps,
        modifier = modifier,
    )
}

/**
 * Shared "curated list" row (Stitch directory_compact mockup, docs/design-ref): a
 * circular leading badge, bold title + optional uppercase category tag, and a
 * truncated subtitle line — used for both cached-local and remote-autocomplete rows
 * since they only differ in which fields feed title/category/subtitle.
 */
@Composable
private fun CuratedPlaceRow(
    title: String,
    category: String?,
    subtitle: String?,
    isImporting: Boolean,
    onImport: () -> Unit,
    onOpenInMaps: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Row(
        modifier = modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(8.dp))
            .background(MaterialTheme.colorScheme.surfaceContainerLow)
            .border(1.dp, MaterialTheme.colorScheme.surfaceVariant, RoundedCornerShape(8.dp))
            .clickable(onClick = onOpenInMaps)
            .padding(12.dp),
        horizontalArrangement = Arrangement.spacedBy(12.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Box(
            modifier = Modifier.size(32.dp).clip(CircleShape).background(MaterialTheme.colorScheme.secondaryContainer),
            contentAlignment = Alignment.Center,
        ) {
            Icon(
                Icons.Default.Place,
                contentDescription = null,
                modifier = Modifier.size(18.dp),
                tint = MaterialTheme.colorScheme.onSecondaryContainer,
            )
        }
        Column(modifier = Modifier.weight(1f)) {
            Row(verticalAlignment = Alignment.Bottom, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                Text(
                    text = title,
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                    modifier = Modifier.weight(1f, fill = false),
                )
                category?.let {
                    Text(
                        text = it.uppercase(),
                        style = MaterialTheme.typography.labelSmall,
                        color = MaterialTheme.colorScheme.secondary,
                    )
                }
            }
            subtitle?.let {
                Text(
                    text = it,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
            }
        }
        ImportButton(isImporting = isImporting, onImport = onImport)
    }
}

@Composable
private fun ImportButton(isImporting: Boolean, onImport: () -> Unit) {
    IconButton(
        onClick = onImport,
        enabled = !isImporting,
    ) {
        if (isImporting) {
            CircularProgressIndicator(modifier = Modifier.height(20.dp))
        } else {
            Icon(
                imageVector = Icons.Default.Add,
                contentDescription = "Add to journal",
            )
        }
    }
}
